import { memo, useState, forwardRef, SyntheticEvent, useImperativeHandle, ReactNode } from 'react'
import Box from '@mui/material/Box'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import useMediaQuery from '@mui/material/useMediaQuery'
import useCallbackDebounced from '../../../hooks/shared/useCallbackDebounced'

export type SplitPreviewLayoutHandles = {
  setTabIndex: React.Dispatch<React.SetStateAction<number>>
}
// eslint-disable-next-line react/display-name
const SplitPreviewLayout = forwardRef<
  SplitPreviewLayoutHandles,
  { id?: string; onClose: () => void; preview: ReactNode; details: ReactNode }
>((props, ref) => {
  const { id, onClose, preview, details } = props
  const md = useMediaQuery(theme => theme.breakpoints.between('xs', 'md'))
  const [tabIndex, setTabIndex] = useState(0)
  useImperativeHandle(ref, () => ({
    setTabIndex,
  }))
  const handleTabChange = useCallbackDebounced((event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }, [])
  return (
    <Box
      id={id}
      sx={theme => ({
        backgroundColor: 'pdfBackground',
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
          overflowY: 'scroll',
        },
        overflow: 'hidden',
      })}
    >
      {md && (
        <Tabs
          centered
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="planning record tabs"
        >
          <Tab label="Details" />
          <Tab label="Preview" />
        </Tabs>
      )}
      <Box
        sx={{
          position: 'relative',
          display: {
            xs: tabIndex == 1 ? 'flex' : 'none',
            md: 'flex',
          },
          flex: 1,
          width: {
            xs: '100%',
            md: '60%',
          },
        }}
      >
        {preview}
      </Box>
      <Box
        sx={{
          display: 'flex',
          width: {
            xs: '100%',
            md: '40%',
          },
          padding: {
            xs: 2,
            md: 0,
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            backgroundColor: 'white',
            borderRadius: {
              xs: 2,
              md: 0,
            },
            display: {
              xs: tabIndex === 0 ? 'flex' : 'none',
              md: 'flex',
            },
            flex: {
              xs: tabIndex === 0 ? 1 : 0,
              md: 1,
            },
            flexDirection: 'column',
            gap: 1,
            padding: {
              xs: 2,
              md: 1,
            },
            overflowY: 'scroll',
          }}
        >
          {details}
        </Box>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          top: 4,
          left: 4,
          zIndex: 2,
        }}
      >
        <IconButton
          aria-label="close"
          color="primary"
          sx={{
            backgroundColor: 'pdfBackground',
            borderRadius: '50%',
            ':hover': {
              backgroundColor: 'pdfBackground',
            },
            ':hover:focus': {
              backgroundColor: 'pdfBackground',
            },
          }}
          onClick={onClose}
          disableRipple
        >
          <CloseIcon sx={{ color: 'black' }} />
        </IconButton>
      </Box>
    </Box>
  )
})

export default memo(SplitPreviewLayout)
